import React from 'react';
import PageHero from '../components/PageHero';
import SourceLink from '../components/SourceLink';
import { SITE, SOURCES } from '../content/site';

const About: React.FC = () => (
  <div>
    <PageHero
      title="About Cardinal Grid"
      lead="I am a load forecasting engineer. Cardinal Grid is what I do with that experience on public data: measure how well U.S. balancing authorities forecast their own load, build open tools for the data problems that make those forecasts fail, and write up what I find so that anyone can check it."
    />

    <section className="bg-white section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">Why I started this</h2>
            <p className="text-lg text-gray-600 mb-4">
              My day job is at a national system operator, where I help produce the load forecasts that feed the
              grid's electrical and energy studies. I know from the inside how a forecast is built, where it breaks,
              and how much of the damage starts with bad data rather than bad models.
            </p>
            <p className="text-lg text-gray-600 mb-4">
              In the United States, the raw material to study this is public. Since 2015 every balancing authority
              has reported to EIA, hour by hour, the demand that occurred and the day-ahead forecast it made the day
              before. FERC and NERC have documented what happens when that forecast is wrong (
              <SourceLink source={SOURCES.elliott} />), and NERC projects the fastest demand growth in two decades,
              most of it data centers (<SourceLink source={SOURCES.ltra2025} />). Nobody had been scoring those
              forecasts continuously and in the open. That is the gap this initiative fills.
            </p>
            <p className="text-lg text-gray-600">
              It is not a company and it does not sell anything. If validated methods are later turned into services
              for utilities, the open tools and notes here stay free.
            </p>
          </div>
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">How I work</h2>
            <ul className="space-y-3 text-gray-700">
              <li>• Public data only, always. Nothing from any employer or client.</li>
              <li>• Every chart comes with the script that made it, so you can rerun it.</li>
              <li>• Results are reported with their limitations, including when a method loses to a baseline.</li>
              <li>• Releases and notes carry DOIs, so a number quoted today can be checked years from now.</li>
              <li>• Corrections are welcome: open an issue on GitHub and I will reprocess and log the change.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-gray-50 section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">About me</h2>
            <p className="text-lg text-gray-700 mb-4">
              I am <strong>{SITE.maintainer.name}</strong>, an electrical engineer with an M.Sc. in Optimization of
              Energy Systems (2023), with a thesis on outlier detection in energy demand curves.
            </p>
            <p className="text-lg text-gray-700 mb-4">
              I have worked on electricity demand forecasting since 2015: in university research, at a distribution
              utility, and now as a load forecasting engineer at Brazil's national system operator. I am a co-author of
              a peer-reviewed method for outlier detection and correction in smart-grid demand data (
              <SourceLink source={SOURCES.energies2024} />) and a co-author of four computer-program registrations at INPI (rights holder of three)
              in forecasting, data correction, economic dispatch and power monitoring.
            </p>
            <p className="text-gray-600 text-sm">
              Everything published here is my own work on public data and does not represent the views of any
              employer.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-4">Find me</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`mailto:${SITE.maintainer.email}`} className="text-primary-red hover:underline">{SITE.maintainer.email}</a>
              </li>
              <li>
                <a href={SITE.github} target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">
                  GitHub · cardinalgrid
                </a>
              </li>
              <li>
                <a href={SITE.maintainer.linkedin} target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">
                  LinkedIn
                </a>
              </li>
              {SITE.maintainer.orcid && (
                <li>
                  <a href={SITE.maintainer.orcid} target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">
                    ORCID 0000-0001-6699-9951
                  </a>
                </li>
              )}
              {SITE.maintainer.scholar && (
                <li>
                  <a href={SITE.maintainer.scholar} target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">
                    Google Scholar
                  </a>
                </li>
              )}
            </ul>
            <h3 className="text-xl font-poppins font-semibold text-dark-gray mt-8 mb-3">Project inbox</h3>
            <p className="text-sm text-gray-600">
              For pilots, data questions or corrections: <a href={`mailto:${SITE.email}`} className="text-primary-red hover:underline">{SITE.email}</a>
            </p>
            <h3 className="text-xl font-poppins font-semibold text-dark-gray mt-8 mb-3">Licenses</h3>
            <p className="text-sm text-gray-600">Code: {SITE.licenses.code}. Notes and reports: {SITE.licenses.notes}.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default About;
